import { Link } from 'react-router-dom';
import { BellSlashIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useFoldResource } from '../hooks/useFoldResource';
import { subscriptionsAPI } from '../services/api';
import { PageHeader, PageState, StaleNotice } from './founder-dashboard/onboarding/outreach/OutreachShared';
import { focusRing } from './founder-dashboard/onboarding/outreach/outreachUtils';
import {
  autoRenewLabel,
  capacity,
  conversionCountdown,
  formatConversionDay,
  offerLabel,
  overcapLabel,
  paymentMethodLabel,
  reminderChoiceLabel,
  reminderStatusLabel,
} from './trials';

const requestTrials = () => subscriptionsAPI.getTrials().then((response) => response.data.data);

const HEADER_CELL = 'px-4 py-3 font-semibold';
const CELL = 'px-4 py-3 align-top';
const SECONDARY = 'mt-0.5 text-xs text-gray-600';

export default function TrialsPage() {
  const [trials, loadTrials] = useFoldResource(requestTrials, 'Trials could not be loaded.');
  const data = trials.status === 'ready' ? trials.payload : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Trials"
        description="Every church and personal plan in a free trial, soonest conversion day first, and the trials that ended in the last 30 days. Days follow Central time."
        refreshing={Boolean(trials.refreshing)}
        onRefresh={loadTrials}
      />
      {trials.status === 'ready' ? <StaleNotice loadedAt={trials.loadedAt} /> : null}

      {trials.status === 'denied' ? (
        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <LockClosedIcon className="mt-0.5 h-6 w-6 flex-none" />
          <p>Fold access is required to see trials.</p>
        </div>
      ) : trials.status === 'failed' ? (
        <LoadFailed message={trials.message} onRetry={loadTrials} />
      ) : (
        <PageState loading={trials.status === 'loading'}>
          {data ? (
            <>
              {trials.refreshError ? (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  Trials could not be refreshed: {trials.refreshError} Showing the last loaded list.
                </div>
              ) : null}
              {data.reminderJob.enabled ? null : <RemindersOffBanner />}
              <ActiveTrials rows={data.active} />
              <EndedTrials rows={data.recentlyEnded} />
            </>
          ) : null}
        </PageState>
      )}
    </div>
  );
}

function LoadFailed({ message, onRetry }) {
  return (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900">
      <p className="font-semibold">Trials could not be loaded</p>
      <p className="mt-1 text-sm">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className={`mt-4 min-h-[44px] rounded-lg border border-current px-4 py-2 text-sm font-semibold ${focusRing}`}
      >
        Try again
      </button>
    </div>
  );
}

function RemindersOffBanner() {
  return (
    <div role="status" className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
      <BellSlashIcon className="mt-0.5 h-5 w-5 flex-none" />
      <div>
        <p className="font-semibold">Trial reminders are off.</p>
        <p className="mt-1">
          No purchaser gets the reminder they chose before their trial converts. Turn on Trial Reminders in{' '}
          <Link to="/cron-jobs" className={`font-semibold underline underline-offset-2 ${focusRing}`}>
            Cron Jobs
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function Section({ id, title, description, empty, children }) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h4 id={id} className="font-semibold text-gray-900">
          {title}
        </h4>
        <p className="mt-1 text-sm text-gray-600">{description}</p>
      </div>
      {empty ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">{empty}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">{children}</div>
      )}
    </section>
  );
}

function OrganizationCell({ row }) {
  return (
    <th scope="row" className={`${CELL} text-left font-medium`}>
      <Link
        to={`/founder-dashboard/organizations/${row.organizationId}`}
        className={`rounded text-blue-700 underline-offset-2 hover:underline ${focusRing}`}
      >
        {row.organizationName}
      </Link>
      <p className={`${SECONDARY} font-normal`}>{row.kind === 'personal' ? 'Personal' : 'Church'}</p>
    </th>
  );
}

function SizeCell({ value }) {
  const size = capacity(value);
  return (
    <td className={`${CELL} text-right tabular-nums ${size.over ? 'font-semibold text-red-700' : 'text-gray-900'}`}>
      {size.text}
      {size.over ? <p className="mt-0.5 text-xs font-normal">Over limit</p> : null}
      {size.detail ? <p className={SECONDARY}>{size.detail}</p> : null}
    </td>
  );
}

function PurchaserCell({ purchaser }) {
  if (!purchaser) {
    return (
      <td className={`${CELL} text-gray-600`}>
        Unknown
        <p className={SECONDARY}>Reminders go to the admins.</p>
      </td>
    );
  }
  return (
    <td className={`${CELL} text-gray-900`}>
      {purchaser.name || purchaser.email}
      <p className={SECONDARY}>
        <a href={`mailto:${purchaser.email}`} className={`rounded hover:underline ${focusRing}`}>
          {purchaser.email}
        </a>
      </p>
    </td>
  );
}

function ActiveTrials({ rows }) {
  return (
    <Section
      id="trials-active-title"
      title="In a trial"
      description="Each trial converts to its paid plan on its conversion day unless auto-renew is off."
      empty={rows.length === 0 ? 'No church or personal plan is in a trial right now.' : null}
    >
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <caption className="sr-only">Trials in progress, soonest conversion day first</caption>
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
          <tr>
            <th scope="col" className={HEADER_CELL}>Organization</th>
            <th scope="col" className={HEADER_CELL}>Started</th>
            <th scope="col" className={HEADER_CELL}>Converts</th>
            <th scope="col" className={HEADER_CELL}>Plan</th>
            <th scope="col" className={HEADER_CELL}>Pays with</th>
            <th scope="col" className={HEADER_CELL}>Reminder</th>
            <th scope="col" className={HEADER_CELL}>Purchaser</th>
            <th scope="col" className={`${HEADER_CELL} text-right`}>Members</th>
            <th scope="col" className={`${HEADER_CELL} text-right`}>Groups</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const renewal = autoRenewLabel(row.autoRenew);
            return (
              <tr key={row.organizationId}>
                <OrganizationCell row={row} />
                <td className={`${CELL} whitespace-nowrap text-gray-900`}>{formatConversionDay(row.startedAt)}</td>
                <td className={`${CELL} whitespace-nowrap text-gray-900`}>
                  {formatConversionDay(row.endsAt)}
                  <p className={`${SECONDARY} ${row.daysLeft <= 1 ? 'font-semibold text-gray-900' : ''}`}>
                    {conversionCountdown(row.daysLeft)}
                  </p>
                </td>
                <td className={`${CELL} text-gray-900`}>
                  {row.plan.name}
                  <p className={SECONDARY}>{offerLabel(row.offer)}</p>
                </td>
                <td className={`${CELL} text-gray-900`}>
                  {paymentMethodLabel(row.paymentMethod)}
                  {renewal ? (
                    <p className={`${SECONDARY} ${row.autoRenew === false ? 'font-semibold text-amber-800' : ''}`}>
                      {renewal}
                    </p>
                  ) : null}
                </td>
                <td className={`${CELL} text-gray-900`}>
                  {reminderChoiceLabel(row.reminder)}
                  <p className={SECONDARY}>{reminderStatusLabel(row.reminder)}</p>
                </td>
                <PurchaserCell purchaser={row.purchaser} />
                <SizeCell value={row.members} />
                <SizeCell value={row.groups} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </Section>
  );
}

function EndedTrials({ rows }) {
  return (
    <Section
      id="trials-ended-title"
      title="Ended in the last 30 days"
      description="Converted means a paid period followed the trial. A church that did not convert keeps everything it has; only new members and groups wait while it is over its limits."
      empty={rows.length === 0 ? 'No trial ended in the last 30 days.' : null}
    >
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <caption className="sr-only">Trials that ended in the last 30 days</caption>
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
          <tr>
            <th scope="col" className={HEADER_CELL}>Organization</th>
            <th scope="col" className={HEADER_CELL}>Ended</th>
            <th scope="col" className={HEADER_CELL}>Plan trialed</th>
            <th scope="col" className={HEADER_CELL}>Result</th>
            <th scope="col" className={HEADER_CELL}>Over limits</th>
            <th scope="col" className={HEADER_CELL}>Purchaser</th>
            <th scope="col" className={`${HEADER_CELL} text-right`}>Members</th>
            <th scope="col" className={`${HEADER_CELL} text-right`}>Groups</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.organizationId}>
              <OrganizationCell row={row} />
              <td className={`${CELL} whitespace-nowrap text-gray-900`}>{formatConversionDay(row.endsAt)}</td>
              <td className={`${CELL} text-gray-900`}>
                {row.plan.name}
                <p className={SECONDARY}>{offerLabel(row.offer)}</p>
              </td>
              <td className={CELL}>
                <span
                  className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                    row.converted
                      ? 'border-green-200 bg-green-50 text-green-800'
                      : 'border-gray-300 bg-gray-100 text-gray-800'
                  }`}
                >
                  {row.converted ? 'Converted' : 'Did not convert'}
                </span>
              </td>
              <td className={`${CELL} text-gray-900`}>{overcapLabel(row.overcapPolicy)}</td>
              <PurchaserCell purchaser={row.purchaser} />
              <SizeCell value={row.members} />
              <SizeCell value={row.groups} />
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}
